import type React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { areaUtils_getBestArea } from 'app/shared/utils/areaUtils';
import { filterUtils_getPropertyForRent } from 'app/shared/utils/filterUtils';
import HamburgerNav from 'app/shared/modules/navigation/header/HamburgerNav';
import ConversationBadge from 'app/shared/modules/ConversationBadge';
import HeaderNavMenuItem from 'app/shared/modules/navigation/header/HeaderNavMenuItem';
import hotpadsLogoWithText from 'images/hotpads-logo-with-text.min.svg';
import Linker from 'app/shared/modules/Linker';
import LoginStatus from 'app/shared/modules/LoginStatus';
import pathsManifest from 'app/shared/utils/pathsManifest';
import routeUtils from 'app/shared/utils/routeUtils';
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';
import {
  headerNavMdAndUpCX,
  headerNavMdAndUpHeaderNavCX,
  headerNavMdAndUpLogoCX,
  headerNavMdAndUpLogoWrapperCX,
  headerNavMdAndUpMenuIconCX,
  headerNavMdAndUpMenuIconSpanCX,
  headerNavMdAndUpNavListCX,
} from 'app/shared/modules/navigation/header/HeaderNavMdAndUp.styles';
import type { RootReduxState } from 'app/shared/flux/reducers';
import ExperimentConstants from 'app/shared/constants/ExperimentConstants';

interface HeaderNavMdAndUpProps {
  onOpenHamburgerNav: () => void;
  onCloseHamburgerNav: () => void;
  unreadMsgCount: number;
}

const HeaderNavMdAndUp: React.FC<HeaderNavMdAndUpProps> = ({
  onOpenHamburgerNav,
  onCloseHamburgerNav,
  unreadMsgCount,
}) => {
  const location = useLocation();

  const { area, hamburgerState, searchSlug, runningExperiments } = useSelector((state: RootReduxState) => ({
    area: areaUtils_getBestArea(state),
    hamburgerState: state.app.hamburger,
    searchSlug: state.filter.search.slug,
    runningExperiments: state.analytics.runningExperiments,
  }));

  const pathname = location.pathname;
  const isAreaPage = routeUtils.isAreaUrl(pathname);

  // HPWEB-6595 A/B TEST - START (Delete when experiment ends)
  const apartmentCondoExperiment = runningExperiments?.[ExperimentConstants.HOTPADS_WEB_APARTMENT_CONDO_SRP];
  let apartmentCondoVariant = null;
  if (apartmentCondoExperiment?.treatment === 'variant1') {
    apartmentCondoVariant = 'variant1';
  } else if (apartmentCondoExperiment?.treatment === 'variant2') {
    apartmentCondoVariant = 'variant2';
  }
  // HPWEB-6595 A/B TEST - END

  const { apartmentsForRent, housesForRent } = filterUtils_getPropertyForRent(area, {
    apartmentCondoVariant, // HPWEB-6595 A/B TEST - Delete when experiment ends (pass null instead)
  });

  // HPWEB-6587: Hide Houses link if user is enrolled in apartment-only experiment
  const isApartmentOnlyExperiment =
    runningExperiments?.[ExperimentConstants.HOTPADS_WEB_APARTMENT_ONLY_LINKS]?.isControl === false;

  return (
    <div className={headerNavMdAndUpCX}>
      <header>
        <nav className={headerNavMdAndUpHeaderNavCX} aria-label="main">
          <button
            className={headerNavMdAndUpMenuIconCX}
            onClick={onOpenHamburgerNav}
            aria-label="HotPads navigation menu"
            aria-expanded={hamburgerState}
          >
            <VisuallyHidden>Navigation menu</VisuallyHidden>
            <span className={headerNavMdAndUpMenuIconSpanCX} />
          </button>
          <HamburgerNav onCloseHamburgerNav={onCloseHamburgerNav} />
          <div className={headerNavMdAndUpLogoWrapperCX}>
            <Linker to={pathsManifest.FrontPage}>
              <img
                src={hotpadsLogoWithText}
                className={headerNavMdAndUpLogoCX}
                alt="HotPads"
                fetchpriority="low"
                decoding="async"
              />
            </Linker>
          </div>
          <ul className={headerNavMdAndUpNavListCX}>
            <HeaderNavMenuItem
              active={isAreaPage && searchSlug === 'apartments-for-rent'}
              mainLinkUrl={apartmentsForRent}
            >
              Apartments
            </HeaderNavMenuItem>
            {!isApartmentOnlyExperiment && (
              <HeaderNavMenuItem active={isAreaPage && searchSlug === 'houses-for-rent'} mainLinkUrl={housesForRent}>
                Houses
              </HeaderNavMenuItem>
            )}
            <HeaderNavMenuItem active={pathname === '/user/listings/my-hub'} mainLinkUrl={pathsManifest.MyHubPage}>
              My Hub
            </HeaderNavMenuItem>
            <HeaderNavMenuItem mainLinkUrl={pathsManifest.InboxPage} escape>
              Messages
              {unreadMsgCount > 0 && <ConversationBadge>{unreadMsgCount}</ConversationBadge>}
            </HeaderNavMenuItem>
            <HeaderNavMenuItem active={pathname === '/user/saved-search'} mainLinkUrl={pathsManifest.SavedSearchPage}>
              Alerts
            </HeaderNavMenuItem>
          </ul>
          <LoginStatus />
        </nav>
      </header>
    </div>
  );
};

export default HeaderNavMdAndUp;
